import { PencilIcon, PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import Button from "components/buttons/Button";
import ButtonWithPopoverDots from "components/buttons/ButtonWithPopoverDots";
import AddContactPersonToClientSidebar from "modules/client/components/AddContactPersonToClientSidebar";
import AddProjectToClientSidebar from "modules/client/components/AddProjectToClientSidebar";
import ClientContactsList from "modules/client/components/ClientDetailsView/components/ClientContactsList";
import ClientsProjectsList from "modules/client/components/ClientDetailsView/components/ClientsProjectsList";
import UpdateClientSidebar from "modules/client/components/UpdateClientSidebar";
import { useCallback, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import BreadcrumbsWrapper from "~/components/BreadcrumbsV2";
import BreadcrumbElement from "~/components/BreadcrumbsV2/components/BreadcrumbElement";
import CompanyLogo from "~/components/CompanyLogo";
import ContentWrapper from "~/components/ContentWrapper";
import SectionHeading from "~/components/headings/SectionHeading";
import SectionHeadingTabs from "~/components/headings/SectionHeadingTabbar";
import Headline from "~/components/Headline";
import MainContent from "~/components/mainContent/MainContent";
import Section from "~/components/sections/Section";
import { CLIENT_DETAILS_PATH, CLIENTS_INDEX_PATH, COMPANY_DETAILS_PATH } from "~/constants/appRoutes.ts";
import type {
	CentralInvoiceRecipient,
} from "~/modules/billing/api/centralInvoiceRecipient/centralInvoiceRecipientTypes.ts";
import type { Client } from "~/modules/client/api/client/clientTypes.ts";
import type { ClientContactPersonType } from "~/modules/client/api/clientContactPerson/clientContactPersonTypes.ts";
import type { Company } from "~/modules/client/api/company/companyTypes.ts";
import type { QuotaWithRelations } from "~/modules/client/api/quota/quotaTypes.ts";
import {
	CLIENT_DETAILS_VIEW_TAB_ID_PARAM_NAME,
	ClientDetailsViewTabId,
} from "~/modules/client/components/ClientDetailsView/clientDetailsViewUtils.ts";
import ClientsBillingDetails from "~/modules/client/components/ClientDetailsView/components/ClientsBillingDetails";
import ClientsInvoiceRecipientsList
	from "~/modules/client/components/ClientDetailsView/components/ClientsInvoiceRecipientsList";
import CreateCentralInvoiceRecipientSidebar
	from "~/modules/client/components/ClientDetailsView/components/ClientsInvoiceRecipientsList/components/CreateCentralInvoiceRecipientSidebar.tsx";
import CreateQuotaSidebar
	from "~/modules/client/components/ClientDetailsView/components/QuotasTabContent/components/CreateQuotaSidebar/CreateQuotaSidebar.tsx";
import QuotasTabContent
	from "~/modules/client/components/ClientDetailsView/components/QuotasTabContent/QuotasTabContent.tsx";
import DeleteClientModal from "~/modules/client/components/DeleteClientModal";
import type { AvatarImageWithClientIds } from "~/modules/image/api/avatarImage/avatarImageTypes.ts";
import type { Project } from "~/modules/project/api/project/projectTypes.ts";
import type { WorkingStatusType as WorkingStatusType } from "~/modules/project/api/workingStatus/workingStatusTypes.ts";

interface ClientViewProps {
	activeTabId: ClientDetailsViewTabId;
	allCompanyAvatarImages?: AvatarImageWithClientIds[];
	clientData: Client;
	company: Company;
	contacts: ClientContactPersonType[];
	invoiceRecipients: CentralInvoiceRecipient[];
	projects: Project[];
	quotas: QuotaWithRelations[];
	workingStatuses: WorkingStatusType[];
}

const ClientDetailsView: React.FC<ClientViewProps> = ({
	activeTabId,
	allCompanyAvatarImages,
	clientData,
	company,
	contacts,
	invoiceRecipients,
	projects,
	quotas,
	workingStatuses,
}) => {
	const navigate = useNavigate();
	const [showNewContactForm, setShowNewContactForm] = useState(false);
	const [showNewInvoiceRecipientForm, setShowNewInvoiceRecipientForm] = useState(false);
	const [showEditForm, setShowEditForm] = useState(false);
	const [showDeletionModal, setShowDeletionModal] = useState(false);
	const [showAddProjectForm, setShowAddProjectForm] = useState(false);
	const [showCreateQuoteSidebar, setShowCreateQuoteSidebar] = useState<boolean>(false);

	const popoverItems = useMemo(() => {
		return [
			{
				label: "Bearbeiten",
				onClick: () => setShowEditForm(true),
				icon: PencilIcon,
			},
			{
				label: "Löschen",
				onClick: () => setShowDeletionModal(true),
				icon: TrashIcon,
				disabled: contacts?.length > 0 || projects?.length > 0 || invoiceRecipients?.length > 0,
			},
		];
	}, [contacts, projects, invoiceRecipients]);
	const avatarImage = allCompanyAvatarImages?.find((image) => image.clientIds.includes(clientData.id));
	const addNewItemButtonTabSection = useMemo(() => {
		if (![ClientDetailsViewTabId.CONTACTS,
			ClientDetailsViewTabId.PROJECTS,
			ClientDetailsViewTabId.INVOICE_RECIPIENTS,
			ClientDetailsViewTabId.QUOTAS,
			ClientDetailsViewTabId.BILLING_DETAILS,
		].includes(activeTabId))
			return null;

		if (activeTabId === ClientDetailsViewTabId.PROJECTS && projects.length === 0) return null;
		if (activeTabId === ClientDetailsViewTabId.CONTACTS && contacts.length === 0) return null;
		if (activeTabId === ClientDetailsViewTabId.INVOICE_RECIPIENTS && invoiceRecipients.length === 0) return null;
		if (activeTabId === ClientDetailsViewTabId.QUOTAS && quotas.length === 0) return null;

		let buttonText = "";
		let onClick = () => {
		};

		switch (activeTabId) {
			case ClientDetailsViewTabId.CONTACTS:
				buttonText = "Neuer Kontakt";
				onClick = () => setShowNewContactForm(true);
				break;
			case ClientDetailsViewTabId.PROJECTS:
				buttonText = "Neues Projekt";
				onClick = () => setShowAddProjectForm(true);
				break;
			case ClientDetailsViewTabId.INVOICE_RECIPIENTS:
				buttonText = "Neuer Rechnungsempfänger";
				onClick = () => setShowNewInvoiceRecipientForm(true);
				break;
			case ClientDetailsViewTabId.QUOTAS:
				buttonText = "Neues Abrechnungskontingent";
				onClick = () => setShowCreateQuoteSidebar(true);
				break;
			default:
				return null;
		}

		return <div>
			<Button size="sm"
					onClick={onClick}>
				<PlusIcon className="size-5"
						  aria-hidden="true" />
				{buttonText}
			</Button>
		</div>;

	}, [activeTabId, projects.length, contacts.length, invoiceRecipients.length, quotas.length]);

	const handleTabClick = useCallback((tabId: ClientDetailsViewTabId) => {
		navigate({
			pathname: generatePath(CLIENT_DETAILS_PATH, { clientId: clientData.id }),
			search: `?${CLIENT_DETAILS_VIEW_TAB_ID_PARAM_NAME}=${tabId}`,
		});
	}, [clientData.id, navigate]);

	return (
		<MainContent>
			<Section className="bg-white">
				<BreadcrumbsWrapper>
					<BreadcrumbElement text="Kunden"
									   url={CLIENTS_INDEX_PATH} />
					<BreadcrumbElement text={company.displayName}
									   url={generatePath(COMPANY_DETAILS_PATH, { companyId: company.id })} />
					<BreadcrumbElement text="Firmen"
									   url={CLIENTS_INDEX_PATH} />
				</BreadcrumbsWrapper>
			</Section>
			<Section className="bg-white pb-2">
				<div className="flex w-full justify-between py-2">
					<div className="flex items-center gap-x-4">
						{avatarImage && <CompanyLogo image={avatarImage} />}
						<Headline type="h2">{clientData.displayName}</Headline>
					</div>
					<ButtonWithPopoverDots
						theme="dark"
						items={popoverItems}
					/>
				</div>
			</Section>
			<div className="flex flex-col gap-y-8">
				<ContentWrapper className="w-full">
					<SectionHeading sticky
									style={{ top: 0 }}>
						<div className="flex w-full items-center justify-between">
							<SectionHeadingTabs
								tabs={[
									{
										name: "Abrechnungsdetails",
										value: ClientDetailsViewTabId.BILLING_DETAILS,
										active: activeTabId === ClientDetailsViewTabId.BILLING_DETAILS,
									},
									{
										name: "Projekte",
										value: ClientDetailsViewTabId.PROJECTS,
										active: activeTabId === ClientDetailsViewTabId.PROJECTS,
									},
									{
										name: "Kontakte",
										value: ClientDetailsViewTabId.CONTACTS,
										active: activeTabId === ClientDetailsViewTabId.CONTACTS,
									},
									{
										name: "Zentrale Rechnungsempfänger",
										value: ClientDetailsViewTabId.INVOICE_RECIPIENTS,
										active: activeTabId === ClientDetailsViewTabId.INVOICE_RECIPIENTS,
									},
									{
										name: "Abrechnungskontingente",
										value: ClientDetailsViewTabId.QUOTAS,
										active: activeTabId === ClientDetailsViewTabId.QUOTAS,
									},
								]}
								onTabClick={handleTabClick}
							/>
							{addNewItemButtonTabSection}
						</div>
					</SectionHeading>

					<div className="mt-4">
						{ClientDetailsViewTabId.BILLING_DETAILS === activeTabId &&
							<ClientsBillingDetails
								clientData={clientData}
							/>}
						{ClientDetailsViewTabId.INVOICE_RECIPIENTS === activeTabId &&
							<ClientsInvoiceRecipientsList
								clientDisplayName={clientData.displayName}
								invoiceRecipients={invoiceRecipients}
								onAddInvoiceRecipientClick={() => setShowNewInvoiceRecipientForm(true)}
							/>}
						{ClientDetailsViewTabId.CONTACTS === activeTabId && contacts && (
							<ClientContactsList
								contacts={contacts}
								displayName={clientData.displayName}
								onAddContactClick={() => setShowNewContactForm(true)}
							/>
						)}

						{ClientDetailsViewTabId.PROJECTS === activeTabId && projects && (
							<ClientsProjectsList
								clientDisplayName={clientData.displayName}
								contacts={contacts}
								projects={projects}
								workingStatuses={workingStatuses}
								onAddProjectClick={() => setShowAddProjectForm(true)}
							/>
						)}

						{ClientDetailsViewTabId.QUOTAS === activeTabId &&
							<QuotasTabContent clientData={clientData}
											  onAddClick={() => setShowCreateQuoteSidebar(true)}
											  quotas={quotas}
							/>}
					</div>
				</ContentWrapper>

				<AddContactPersonToClientSidebar
					isOpen={showNewContactForm}
					setOpen={setShowNewContactForm}
					clientId={clientData.id}
					companyId={clientData.companyId}
				/>

				<CreateCentralInvoiceRecipientSidebar clientId={clientData.id}
													  isOpen={showNewInvoiceRecipientForm}
													  onClose={() => setShowNewInvoiceRecipientForm(false)} />

				<UpdateClientSidebar isOpen={showEditForm}
									 setOpen={setShowEditForm}
									 clientData={clientData} />


				<AddProjectToClientSidebar isOpen={showAddProjectForm}
										   setOpen={setShowAddProjectForm}
										   clientId={clientData.id} />

				<DeleteClientModal clientId={clientData.id}
								   isOpen={showDeletionModal}
								   onClose={() => setShowDeletionModal(false)} />
				<CreateQuotaSidebar clientId={clientData.id}
									isOpen={showCreateQuoteSidebar}
									onClose={() => setShowCreateQuoteSidebar(false)} />
			</div>
		</MainContent>
	);
};

export default ClientDetailsView;
